import React, { useEffect, useState } from "react";
import { useStore } from "react-redux";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchEncounterByReference } from "../services/encounterService";
import { fetchOrganizationByReference } from "../services/organizationService";
import { fetchSpecimenByReference } from "../services/specimenService";
import styles from "../modals/ResourceViewerModal.module.css";
import localStyles from "./SpecimenCardExtended.module.css";
import { formatLocalDate } from "../utils/dateUtils";
import FemalePatient from "../assets/Patient_Female_96.png";
import MalePatient from "../assets/Patient_Male_96.png";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male_96.png";
import DefaultOrg from "../assets/world-health.png";
import { resolveName } from "../utils/messageUtils";
import { Field, SectionHeader, renderCodeable } from "../utils/renderUtils";
import CardHeader from "./CardHeader";

const SpecimenCardExtended = ({ resource, handleShowNested, fhirClient }) => {
    const store = useStore();
    const [subject, setSubject] = useState(null);
    const [collector, setCollector] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [encounterData, setEncounterData] = useState(null);
    const [parentSpecimens, setParentSpecimens] = useState([]);

    // Fetch references
    useEffect(() => {
        if (!resource || !fhirClient) return;

        const fetchRefs = async () => {
            try {
                if (resource.subject?.reference) {
                    const s = await fetchPatientByReference(resource.subject.reference, fhirClient, store);
                    setSubject(s);
                }

                const collRef = resource.collection?.collector?.reference;
                if (collRef) {
                    const c = await fetchProviderByReference(collRef, fhirClient, store);
                    setCollector(c);
                }

                const encRef = resource.collection?.encounter?.reference;
                if (encRef) {
                    const e = await fetchEncounterByReference(encRef, fhirClient, store);
                    setEncounterData(e);
                }

                const orgRef = resource.collection?.organization?.reference;
                if (orgRef) {
                    const o = await fetchOrganizationByReference(orgRef, fhirClient, store);
                    setOrganization(o);
                }

                if (resource.parent?.length) {
                    const parents = await Promise.all(
                        resource.parent.map((p) => fetchSpecimenByReference(p.reference, fhirClient, store))
                    );
                    setParentSpecimens(parents.filter(Boolean));
                }
            } catch (err) {
                console.error("SpecimenCardExtended: failed to fetch references", err);
            }
        };

        fetchRefs();
    }, [resource, fhirClient, store]);

  
    return (
        <div className={`p-4 border rounded shadow-sm bg-white`}>
            <CardHeader resource={resource} handleShowNested={handleShowNested} fhirClient={fhirClient} />



            {/* IDENTIFICATION */}
            <SectionHeader>Identification</SectionHeader>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <Field label="Specimen ID">{resource.id}</Field>
                    <Field label="Status">{resource.status}</Field>
                    <Field label="Type">{renderCodeable(resource.type)}</Field>
                    <Field label="Condition">{renderCodeable(resource.condition?.[0])}</Field>
                    <Field label="Role">{resource.role}</Field>
                </div></div>

            {/* COLLECTION */}
            <SectionHeader>Collection</SectionHeader>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <Field label="Collected Date/Time">{formatLocalDate(resource.collection?.collectedDateTime)}</Field>
                    <Field label="Body Site">{renderCodeable(resource.collection?.bodySite)}</Field>
                    <Field label="Method">{renderCodeable(resource.collection?.method)}</Field>
                    <Field label="Fasting Status">{renderCodeable(resource.collection?.fastingStatusCodeableConcept)}</Field>
                    <Field label="Quantity">{resource.collection?.quantity?.value} {resource.collection?.quantity?.unit}</Field>
                </div></div>

            {/* PROCESSING */}
            {resource.processing?.length > 0 && (
                <>
                    <SectionHeader>Processing</SectionHeader>
                    <div className={styles.indented}>
                        {resource.processing.map((proc, idx) => (
                            <div key={idx} className="mb-2 border rounded p-2 bg-light">
                                <Field label="Description">{proc.description}</Field>
                                <Field label="Procedure">{renderCodeable(proc.procedure)}</Field>
                                <Field label="Time">{formatLocalDate(proc.timeDateTime)}</Field>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* METADATA */}
            <SectionHeader>Metadata</SectionHeader>
            <div className={styles.indented}>
                <Field label="FHIR Resource Type">{resource.resourceType}</Field>
                <Field label="Meta Version">{resource.meta?.versionId}</Field>
                <Field label="Last Updated">{resource.meta?.lastUpdated ? formatLocalDate(resource.meta.lastUpdated) : "N/A"}</Field>
            </div>
        </div>
    );
};

export default SpecimenCardExtended;
